import React, { useMemo, useState } from "react";
import Modal from "@mui/material/Modal";
import { Box } from "@mui/material";
import XCloseIcon from "assets/icons/HeroIcons/XCloseIcon";
import { RootState } from "store";
import { useDispatch, useSelector } from "react-redux";
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { useParams } from "react-router-dom";
import apiLibrary from "services/api";
import Button from "view/components/Button";
import { Toasts } from "view/components/Toasts";
import { closeBulkArchiveObservationModalAction } from "store/modals/reducer.actions";
import { fetchObservationsAction } from "store/observations";
import { observationsStatusAction } from "store/filters";

const style: React.CSSProperties = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  borderRadius: "10px",
  backgroundColor: "#ffff",
};

export const BulkArchiveObservationModal: React.FC = () => {
  const { bulkArchiveObservationModal } = useSelector(
    (state: RootState) => state.modals,
  );
  const modalData = bulkArchiveObservationModal?.data ?? {};
  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const { communityId, programId } = useParams();
  const selectedObservations = modalData.observations ?? [];
  const selectedObservationIds = modalData.ids ?? [];
  const filteredObservationIds = useMemo(() => {
    if (!selectedObservations.length) {
      return selectedObservationIds;
    }

    return selectedObservations
      .filter((observation: any) => observation?.programStatus !== "completed")
      .map((observation: any) => observation?.observationCode?.id ?? observation?.id)
      .filter((id: any) => id !== undefined && id !== null);
  }, [selectedObservations, selectedObservationIds]);

  const handleClose = () => {
    dispatch(closeBulkArchiveObservationModalAction());
  };

  const archiveObservations = async () => {
    if (filteredObservationIds.length === 0) {
      Toasts.error("Please select at least one observation from the table");
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiLibrary.Observations.bulkArchiveObservations(
        filteredObservationIds,
        "archived",
      );

      Toasts.success(response?.message);

      dispatch(observationsStatusAction("archived"));
      dispatch(fetchObservationsAction(communityId, programId));
      handleClose();
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message ?? error?.message;
      Toasts.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={bulkArchiveObservationModal?.isOpen}
      onClose={handleClose}
      aria-labelledby="bulk-archive-observations-modal-title"
      disableAutoFocus={true}
      aria-describedby="bulk-archive-observations-modal-description"
      className="border-none"
    >
      <Box sx={style} className="dark:bg-secondaryLight">
        <div className="flex flex-col justify-start items-start w-[480px] rounded-lg">
          <div
            className="relative flex flex-col items-start self-stretch justify-start flex-grow-0 flex-shrink-0 gap-4 p-6 rounded-lg dark:bg-secondaryLight bg-bgWhite"
            style={{
              boxShadow:
                "0px 2px 8px 0 rgba(2,13,36,0.1), 0px 2px 24px 0 rgba(2,13,36,0.08)",
            }}
          >
            <div className="flex justify-between items-start self-stretch flex-grow-0 flex-shrink-0 py-0.5">
              <div className="relative flex items-center self-stretch justify-start flex-grow-0 flex-shrink-0">
                <p className="flex-grow text-xl font-semibold text-left text-textMid dark:text-textMain">
                  Archive Observations
                </p>
              </div>
              <button onClick={handleClose} type="button">
                <XCloseIcon width={24} height={24} viewBox="0 0 24 24" />
              </button>
            </div>
            <div className="flex justify-start items-start self-stretch flex-grow-0 flex-shrink-0 relative gap-2 pb-1.5">
              <p className="flex-grow w-[432px] text-sm text-left text-textMid dark:text-textMain">
                Are you sure you want to archive these{" "}
                {filteredObservationIds.length} observations? You can restore
                them later from the archived tab.
              </p>
            </div>
            <div className="flex items-center self-stretch justify-end flex-grow-0 flex-shrink-0 gap-2">
              <Button
                type="button"
                text="Cancel"
                filledColor="primary"
                outlinedColor="primary"
                textColor="textWhite"
                className="px-5 h-11"
                width="35"
                height="13"
                fontStyle="font-semibold"
                variant="outlined"
                onClick={handleClose}
              />
              <Button
                disabled={isLoading || filteredObservationIds.length === 0}
                type="button"
                text="Yes, Archive All"
                filledColor="primary"
                outlinedColor="primary"
                textColor="textWhite"
                className="px-5 h-11"
                width="35"
                height="13"
                fontStyle="font-semibold"
                variant="filled"
                onClick={archiveObservations}
              />
            </div>
          </div>
        </div>
      </Box>
    </Modal>
  );
};
